import type { WingSpec } from '@roomtreatment/core';

/** Closed floor-polygon vertex — same shape as {@link Point2D} in index.ts. */
export interface PolyPt { x: number; y: number }

const EPS = 1e-6;

/** The floor polygon of a rectangular room extended by wings. Falls back to
 *  the plain box when there are no usable wings. */
export function wingsToPolygon(Lx: number, Ly: number, wings: WingSpec[]): PolyPt[] {
  const box = (): PolyPt[] => [
    { x: 0, y: 0 }, { x: Lx, y: 0 }, { x: Lx, y: Ly }, { x: 0, y: Ly },
  ];
  const clean = wings
    .map((w) => ({
      wall: w.wall, depth: +w.depth || 0, width: +w.width || 0, offset: +w.offset || 0,
    }))
    .filter((w) => w.depth > 0 && w.width > 0);
  if (!clean.length) return box();

  const byWall: Record<string, typeof clean> = { left: [], back: [], right: [], front: [] };
  for (const w of clean) (byWall[w.wall] ?? (byWall[w.wall] = [])).push(w);
  for (const k of Object.keys(byWall)) byWall[k].sort((a, b) => a.offset - b.offset);

  // Walk the perimeter CCW: [name, walkAxis, perpVal, from, to, wingDir].
  const walls: [string, 'x' | 'y', number, number, number, number][] = [
    ['left', 'x', 0, 0, Lx, -1],
    ['back', 'y', Lx, 0, Ly, 1],
    ['right', 'x', Ly, Lx, 0, 1],
    ['front', 'y', 0, Ly, 0, -1],
  ];
  const pt = (walkVal: number, perpVal: number, ax: 'x' | 'y'): PolyPt =>
    (ax === 'x' ? { x: walkVal, y: perpVal } : { x: perpVal, y: walkVal });

  const fp: PolyPt[] = [];
  for (const [name, ax, perp, from, to, dir] of walls) {
    let ww = byWall[name] ?? [];
    const fwd = from < to ? 1 : -1;
    if (fwd === -1) ww = [...ww].reverse();
    let cursor = from;
    for (const w of ww) {
      let ws = w.offset, we = w.offset + w.width;
      if (fwd === -1) { const t = ws; ws = we; we = t; }
      if (Math.abs(cursor - ws) > EPS) { fp.push(pt(cursor, perp, ax)); cursor = ws; }
      fp.push(pt(ws, perp, ax));
      const ext = perp + dir * w.depth;
      fp.push(pt(ws, ext, ax));
      fp.push(pt(we, ext, ax));
      fp.push(pt(we, perp, ax));
      cursor = we;
    }
    if (Math.abs(cursor - to) > EPS || !ww.length) fp.push(pt(cursor, perp, ax));
  }

  // Drop consecutive duplicate vertices.
  const out: PolyPt[] = fp.length ? [fp[0]] : [];
  for (let i = 1; i < fp.length; i++) {
    const p = fp[i], q = out[out.length - 1];
    if (Math.abs(p.x - q.x) > EPS || Math.abs(p.y - q.y) > EPS) out.push(p);
  }
  return out.length >= 3 ? out : box();
}

/** Longest wall (edge) of a closed floor polygon, in metres. */
export function longestWallLength(poly: PolyPt[]): number {
  let max = 0;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len > max) max = len;
  }
  return max;
}

/** Axis-aligned bounding box of a floor polygon. Every consumer that needs
 *  "the room's outer extent" (as opposed to its true, possibly non-
 *  rectangular boundary) re-derives this by hand today — this is the shared
 *  version new callers should reach for instead of a fifth private copy. For
 *  an L-shaped room (wings/footprint) this is the AABB, not the true
 *  boundary, so a point at one of its four corners may sit outside the real
 *  room — same known approximation `panelSurface.ts`'s ceiling/floor frames
 *  already accept. */
export function polyBounds(poly: PolyPt[]): { minX: number; maxX: number; minY: number; maxY: number } {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of poly) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, maxX, minY, maxY };
}
